import { type InputHTMLAttributes, type ReactNode, useId } from 'react';
import { phrase } from '../lib/i18n/runtime';

function phraseNode(node: ReactNode): ReactNode {
  return typeof node === 'string' ? phrase(node) : node;
}

interface FieldShellProps {
  readonly label: string;
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
  readonly required?: boolean;
  readonly children: (ids: { id: string; describedBy: string | undefined }) => ReactNode;
}

function FieldShell({ label, error, hint, required, children }: FieldShellProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy =
    [hint !== undefined ? hintId : null, error !== undefined ? errorId : null]
      .filter((value) => value !== null)
      .join(' ') || undefined;
  return (
    <div className={`field${error !== undefined ? ' field--invalid' : ''}`}>
      <label htmlFor={id} className="field__label">
        {phrase(label)}
        {required === true && <span className="field__required"> {phrase('(required)')}</span>}
      </label>
      {hint !== undefined && (
        <p id={hintId} className="field__hint">
          {phraseNode(hint)}
        </p>
      )}
      {children({ id, describedBy })}
      {error !== undefined && (
        <p id={errorId} className="field__error">
          <span aria-hidden="true">⚠ </span>
          {phrase(error)}
        </p>
      )}
    </div>
  );
}

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'onChange' | 'value'>;

export interface TextFieldProps extends InputProps {
  readonly label: string;
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
}

export function TextField({
  label,
  value,
  onValueChange,
  error,
  hint,
  required,
  ...inputProps
}: TextFieldProps) {
  return (
    <FieldShell label={label} error={error} hint={hint} required={required ?? false}>
      {({ id, describedBy }) => (
        <input
          {...inputProps}
          id={id}
          className="input"
          value={value}
          required={required}
          aria-invalid={error !== undefined}
          aria-describedby={describedBy}
          onChange={(event) => {
            onValueChange(event.target.value);
          }}
        />
      )}
    </FieldShell>
  );
}

export interface TextAreaFieldProps {
  readonly label: string;
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
  readonly required?: boolean;
  readonly rows?: number;
  readonly maxLength?: number;
}

export function TextAreaField({
  label,
  value,
  onValueChange,
  error,
  hint,
  required,
  rows = 3,
  maxLength,
}: TextAreaFieldProps) {
  return (
    <FieldShell label={label} error={error} hint={hint} required={required ?? false}>
      {({ id, describedBy }) => (
        <textarea
          id={id}
          className="input"
          rows={rows}
          value={value}
          required={required}
          maxLength={maxLength}
          aria-invalid={error !== undefined}
          aria-describedby={describedBy}
          onChange={(event) => {
            onValueChange(event.target.value);
          }}
        />
      )}
    </FieldShell>
  );
}

export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

export interface SelectFieldProps {
  readonly label: string;
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly options: readonly SelectOption[];
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
  readonly required?: boolean;
  readonly disabled?: boolean;
  /** Adds an empty first option with this label. */
  readonly placeholder?: string;
}

export function SelectField({
  label,
  value,
  onValueChange,
  options,
  error,
  hint,
  required,
  disabled,
  placeholder,
}: SelectFieldProps) {
  return (
    <FieldShell label={label} error={error} hint={hint} required={required ?? false}>
      {({ id, describedBy }) => (
        <select
          id={id}
          className="input"
          value={value}
          required={required}
          disabled={disabled}
          aria-invalid={error !== undefined}
          aria-describedby={describedBy}
          onChange={(event) => {
            onValueChange(event.target.value);
          }}
        >
          {placeholder !== undefined && <option value="">{phrase(placeholder)}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {phrase(option.label)}
            </option>
          ))}
        </select>
      )}
    </FieldShell>
  );
}

export function CheckboxField({
  label,
  checked,
  onCheckedChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  hint?: ReactNode;
}) {
  const id = useId();
  return (
    <div className="field field--checkbox">
      <input
        id={id}
        type="checkbox"
        className="checkbox"
        checked={checked}
        aria-describedby={hint !== undefined ? `${id}-hint` : undefined}
        onChange={(event) => {
          onCheckedChange(event.target.checked);
        }}
      />
      <label htmlFor={id}>{phrase(label)}</label>
      {hint !== undefined && (
        <p id={`${id}-hint`} className="field__hint">
          {phraseNode(hint)}
        </p>
      )}
    </div>
  );
}
